import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { ThumbButton } from '../components/ThumbButton.js';

const { t } = i18n;

interface LowStockRow {
  id: string;
  code: string;
  nameLt: string;
  type: 'PRODUCT' | 'COMPONENT';
  lowWaterQty: number | null;
  onHand: number;
  photoUrl: string | null;
}

export function LowStockPage() {
  const { partnerId = '' } = useParams();
  const [rows, setRows] = useState<LowStockRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<LowStockRow[]>(`/partners/${partnerId}/stock/low`)
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [partnerId]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!rows) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-4">
      <div>
        <Link
          to={`/partners/${partnerId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          ← {t('partner.list.title')}
        </Link>
        <h2 className="text-xl font-semibold mt-1">{t('stock.low.title')}</h2>
      </div>

      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t('stock.low.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="w-14 px-3 py-2" aria-label={t('photo.title')} />
                <th className="px-3 py-2 font-medium">{t('sku.col.code')}</th>
                <th className="px-3 py-2 font-medium">{t('sku.col.name')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('stock.col.on_hand')}</th>
                <th className="px-3 py-2 font-medium text-right">{t('sku.col.low_water')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <ThumbButton url={row.photoUrl} alt={`${row.code} — ${row.nameLt}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/partners/${partnerId}/skus/${row.id}`} className="hover:underline">
                      {row.code}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{row.nameLt}</td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      row.onHand <= 0 ? 'text-red-600' : 'text-amber-700 dark:text-amber-300'
                    }`}
                  >
                    {row.onHand.toLocaleString('lt-LT')}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                    {row.lowWaterQty?.toLocaleString('lt-LT') ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
